// src/cockpit4/components/AssetBuildingDashboard.tsx
// ============================================
// 资产建设仪表板 - Phase III 核心入口
//
// 五大资产建设方向：
//   1. Knowledge Factory（知识工厂）- 500+ Cards 目标
//   2. Software Operation（软件运营资产化）- 5 大产品白名单
//   3. 知识飞轮（Knowledge Flywheel）- 软件→知识自动沉淀
//   4. Brand Bible（品牌圣经）- 5 大品牌规范
//   5. CEO Brain（CEO 决策大脑）- 每日战略建议
//
// 显示每个方向的：进度、关键指标、最近活动、下一步行动
// ============================================

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Boxes,
  RefreshCw,
  Palette,
  Brain,
  TrendingUp,
  Loader2,
  ChevronRight,
  X,
  Target,
  CheckCircle2,
  CircleDashed,
  ArrowUpRight,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

type AssetCategory = 'knowledge_factory' | 'software_operation' | 'knowledge_flywheel' | 'brand_bible' | 'ceo_brain';

interface AssetMetric {
  label: string;
  value: string;
  target?: string;
}

interface AssetDirection {
  id: AssetCategory;
  name: string;
  englishName: string;
  description: string;
  icon: typeof BookOpen;
  color: string;
  progress: number;
  status: 'completed' | 'in_progress' | 'at_risk' | 'pending';
  metrics: AssetMetric[];
  recentActivities: string[];
  nextActions: string[];
}

interface AssetDashboardData {
  overallProgress: number;
  directions: AssetDirection[];
  lastUpdated: string;
}

// ============================================
// 从 Obsidian 加载真实数据
// ============================================

/**
 * 从 Obsidian 年度目标文件 + Cards 目录统计获取真实数据
 *
 * 数据源：
 *   1. 11_Strategy（战略层）/年度目标-2026.md - 年度目标进度
 *   2. 09_Knowledge（知识中心）/Cards/ 各领域子目录 - Cards 计数
 *   3. 08_Brand（品牌中心）/ - 品牌规范文件存在性
 *   4. 09_Knowledge（知识中心）/Business/ - 产品白名单文件
 */
async function loadRealDashboardData(): Promise<AssetDashboardData> {
  const defaultData = getDefaultDashboardData();

  try {
    // 1. 读取年度目标文件
    let annualContent = '';
    const annualPaths = [
      '11_Strategy（战略层）/年度目标-2026.md',
      '10_Operations（运营层）/年度目标-2026.md',
    ];
    for (const p of annualPaths) {
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && result.content) {
            annualContent = result.content;
            break;
          }
        }
      } catch {
        // 继续
      }
    }

    // 2. 读取 Cards 各领域目录
    const domainCounts: Record<string, number> = {};
    const domains = ['Agent', 'CRM', 'LLM', 'Marketing', 'Quant', 'Sales', 'Workflow', 'Data', 'Engineering', 'Product', 'MCP', 'SaaS', '酒店', '股票', '黄金'];
    let totalCards = 0;
    for (const domain of domains) {
      try {
        const resp = await fetch(`/vault-api/list?path=${encodeURIComponent(`09_Knowledge（知识中心）/Cards/${domain}`)}`);
        if (resp.ok) {
          const result = await resp.json();
          const files = result.files || result.data?.files || [];
          // 只计算 .md 文件，排除 _INDEX.md
          const mdFiles = files.filter((f: any) => {
            const name = typeof f === 'string' ? f : f.name || f.filename || '';
            return name.endsWith('.md') && !name.startsWith('_INDEX');
          });
          domainCounts[domain] = mdFiles.length;
          totalCards += mdFiles.length;
        }
      } catch {
        // 继续
      }
    }

    // 3. 读取品牌规范文件
    const brandFiles = ['Brand_Bible_GoldFactory.md', 'Brand_Bible_HotelMind.md', 'Brand_Bible_XuanJi.md'];
    const brandStatus: Record<string, boolean> = {};
    for (const file of brandFiles) {
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(`08_Brand（品牌中心）/${file}`)}`);
        brandStatus[file] = resp.ok;
      } catch {
        brandStatus[file] = false;
      }
    }

    // 4. 读取产品白名单文件
    const productFiles = [
      '股票量化交易系统白名单.md',
      '酒店SaaS产品白名单.md',
      '黄金外汇量化交易软件白名单.md',
      'AI流量趋势追踪软件白名单.md',
      '专业算命软件工具白名单.md',
    ];
    let productCount = 0;
    for (const file of productFiles) {
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(`09_Knowledge（知识中心）/Business/${file}`)}`);
        if (resp.ok) productCount++;
      } catch {
        // 继续
      }
    }

    // 5. 解析年度目标文件中的进度数据
    const parsed = annualContent ? parseAnnualProgress(annualContent) : null;

    // 6. 构建真实数据
    return buildRealDashboardData({
      domainCounts,
      totalCards,
      brandStatus,
      productCount,
      annualProgress: parsed,
    });
  } catch (err) {
    console.warn('[AssetBuildingDashboard] 加载真实数据失败，使用默认数据:', err);
    return defaultData;
  }
}

/**
 * 从年度目标文件中解析各方向进度
 */
function parseAnnualProgress(content: string): Record<string, number> {
  const progress: Record<string, number> = {};
  // 匹配总体目标表格：| Knowledge Factory | 500+ Cards 知识资产 | 30% | 进行中 |
  const lines = content.split('\n');
  for (const line of lines) {
    if (line.startsWith('|') && line.includes('%')) {
      const cells = line.split('|').map(c => c.trim()).filter(Boolean);
      if (cells.length >= 3) {
        const name = cells[0];
        const progressMatch = cells.find(c => c.match(/\d+%/));
        if (progressMatch) {
          const pct = parseInt(progressMatch.match(/(\d+)%/)?.[1] || '0');
          if (name.includes('Knowledge Factory')) progress.knowledge_factory = pct;
          else if (name.includes('Software Operation')) progress.software_operation = pct;
          else if (name.includes('知识飞轮') || name.includes('Flywheel')) progress.knowledge_flywheel = pct;
          else if (name.includes('Brand Bible')) progress.brand_bible = pct;
          else if (name.includes('CEO Brain')) progress.ceo_brain = pct;
        }
      }
    }
  }
  return progress;
}

/**
 * 基于真实数据构建仪表板数据
 */
function buildRealDashboardData(real: {
  domainCounts: Record<string, number>;
  totalCards: number;
  brandStatus: Record<string, boolean>;
  productCount: number;
  annualProgress: Record<string, number> | null;
}): AssetDashboardData {
  const defaults = getDefaultDashboardData();
  const annualProgress = real.annualProgress || {};

  // Knowledge Factory - 基于真实 Cards 计数
  const knowledgeDir = defaults.directions.find(d => d.id === 'knowledge_factory')!;
  const domainMetrics: AssetMetric[] = Object.entries(real.domainCounts)
    .filter(([_, count]) => count > 0)
    .slice(0, 8)
    .map(([domain, count]) => ({
      label: `${domain} 领域`,
      value: String(count),
      target: '50',
    }));
  if (real.totalCards > 0) {
    domainMetrics.push({ label: '累计总数', value: String(real.totalCards), target: '500+' });
  }
  knowledgeDir.metrics = domainMetrics.length > 0 ? domainMetrics : knowledgeDir.metrics;
  knowledgeDir.progress = annualProgress.knowledge_factory ?? (real.totalCards > 0 ? Math.min(100, Math.round((real.totalCards / 500) * 100)) : knowledgeDir.progress);

  // Software Operation - 基于真实白名单文件计数
  const softwareDir = defaults.directions.find(d => d.id === 'software_operation')!;
  softwareDir.metrics = [
    { label: '股票量化', value: '✓ 完成', target: 'tradehetech.com' },
    { label: '酒店 SaaS', value: '✓ 完成', target: 'HotelMind' },
    { label: '黄金外汇', value: '✓ 完成', target: 'GoldFactory' },
    { label: 'AI 流量趋势', value: '✓ 完成', target: '集成 XEAOS' },
    { label: '专业算命', value: '✓ 完成', target: 'xuanji.xshetech.com' },
    { label: '白名单总数', value: `${real.productCount}/5`, target: '5' },
  ];
  softwareDir.progress = annualProgress.software_operation ?? Math.min(100, Math.round((real.productCount / 5) * 100));

  // Brand Bible - 基于真实品牌文件存在性
  const brandDir = defaults.directions.find(d => d.id === 'brand_bible')!;
  const brandCount = Object.values(real.brandStatus).filter(Boolean).length;
  brandDir.metrics = [
    { label: 'XEAOS', value: '定位完成', target: '完整规范' },
    { label: '小舍AI', value: '定位完成', target: '完整规范' },
    { label: 'HotelMind', value: real.brandStatus['Brand_Bible_HotelMind.md'] ? '✓ 完成' : '待启动', target: '完整规范' },
    { label: 'GoldFactory', value: real.brandStatus['Brand_Bible_GoldFactory.md'] ? '✓ 完成' : '待启动', target: '完整规范' },
    { label: '玄机', value: real.brandStatus['Brand_Bible_XuanJi.md'] ? '✓ 完成' : '待启动', target: '完整规范' },
    { label: '品牌总数', value: `${2 + brandCount}/5`, target: '5' },
  ];
  brandDir.progress = annualProgress.brand_bible ?? Math.min(100, Math.round(((2 + brandCount) / 5) * 100));

  // CEO Brain / Knowledge Flywheel 使用年度目标中的进度
  const ceoDir = defaults.directions.find(d => d.id === 'ceo_brain')!;
  ceoDir.progress = annualProgress.ceo_brain ?? ceoDir.progress;
  const flywheelDir = defaults.directions.find(d => d.id === 'knowledge_flywheel')!;
  flywheelDir.progress = annualProgress.knowledge_flywheel ?? flywheelDir.progress;

  // 总进度
  const allProgress = defaults.directions.map(d => d.progress);
  const overallProgress = Math.round(allProgress.reduce((s, p) => s + p, 0) / allProgress.length);

  return {
    overallProgress,
    lastUpdated: new Date().toISOString(),
    directions: defaults.directions,
  };
}

// ============================================
// 默认数据（基于年度目标文件）
// ============================================

function getDefaultDashboardData(): AssetDashboardData {
  return {
    overallProgress: 35,
    lastUpdated: new Date().toISOString(),
    directions: [
      {
        id: 'knowledge_factory',
        name: 'Knowledge Factory',
        englishName: '知识工厂',
        description: '500+ 高质量知识 Cards，覆盖 10 大知识领域',
        icon: BookOpen,
        color: '#3B82F6',
        progress: 30,
        status: 'in_progress',
        metrics: [
          { label: 'Agent 领域', value: '29', target: '50' },
          { label: 'CRM 领域', value: '30', target: '50' },
          { label: 'LLM 领域', value: '29', target: '50' },
          { label: 'Marketing 领域', value: '30', target: '50' },
          { label: 'Quant 领域', value: '29', target: '50' },
          { label: 'Sales 领域', value: '30', target: '50' },
          { label: 'Workflow 领域', value: '28', target: '50' },
          { label: '累计总数', value: '~240', target: '500+' },
        ],
        recentActivities: [
          '新增 MCP 协议工作原理 Card',
          '新增 MCP 安全与权限设计 Card',
          'CRM 领域完成 30 篇（达标）',
        ],
        nextActions: [
          'Data 领域补充至 50 篇（当前 11）',
          'Engineering 领域补充至 50 篇（当前 12）',
          'Product 领域补充至 50 篇（当前 11）',
        ],
      },
      {
        id: 'software_operation',
        name: 'Software Operation',
        englishName: '软件运营资产化',
        description: '5 大产品白名单 + 软件资产化运营',
        icon: Boxes,
        color: '#10B981',
        progress: 80,
        status: 'in_progress',
        metrics: [
          { label: '股票量化', value: '✓ 完成', target: 'tradehetech.com' },
          { label: '酒店 SaaS', value: '✓ 完成', target: 'HotelMind' },
          { label: '黄金外汇', value: '✓ 完成', target: 'GoldFactory' },
          { label: 'AI 流量趋势', value: '✓ 完成', target: '集成 XEAOS' },
          { label: '专业算命', value: '✓ 完成', target: 'xuanji.xshetech.com' },
          { label: '白名单总数', value: '5/5', target: '5' },
        ],
        recentActivities: [
          '酒店 SaaS 白名单补充到 product_whitepapers',
          '5 大产品白名单全部同步到 Obsidian',
          'API 热插拔配置系统上线',
        ],
        nextActions: [
          '5 大产品 XEAOS 集成度提升至 80%',
          'AI 流量趋势软件集成到 XEAOS 企业版',
          '黄金外汇量化交易软件产品化',
        ],
      },
      {
        id: 'knowledge_flywheel',
        name: 'Knowledge Flywheel',
        englishName: '知识飞轮',
        description: '软件操作→自动生成知识卡片→反哺决策→优化软件',
        icon: RefreshCw,
        color: '#8B5CF6',
        progress: 20,
        status: 'in_progress',
        metrics: [
          { label: '软件操作沉淀', value: '15 篇', target: '100' },
          { label: 'GEO 内容资产', value: '30%', target: '50 篇' },
          { label: '飞轮闭环度', value: '20%', target: '100%' },
          { label: '自动沉淀机制', value: '设计中', target: '上线' },
        ],
        recentActivities: [
          'GEO+DSO 内容资产生成入口上线',
          '媒体工作室脚本编辑功能完成',
          '内容计划自动派生机制建立',
        ],
        nextActions: [
          '设计软件操作日志→LLM 提取知识点流程',
          '建立知识卡片→CEO Brain 决策建议通路',
          'GEO 内容资产库扩充至 50 篇',
        ],
      },
      {
        id: 'brand_bible',
        name: 'Brand Bible',
        englishName: '品牌圣经',
        description: '5 大品牌完整规范，统一视觉与语调',
        icon: Palette,
        color: '#EC4899',
        progress: 25,
        status: 'in_progress',
        metrics: [
          { label: 'XEAOS', value: '定位完成', target: '完整规范' },
          { label: '小舍AI', value: '定位完成', target: '完整规范' },
          { label: 'HotelMind', value: '待启动', target: '完整规范' },
          { label: 'GoldFactory', value: '待启动', target: '完整规范' },
          { label: '玄机', value: '待启动', target: '完整规范' },
          { label: '品牌总数', value: '2/5', target: '5' },
        ],
        recentActivities: [
          'XEAOS 品牌定位文档完成',
          '小舍AI 品牌定位文档完成',
          'Brand Bible 框架设计完成',
        ],
        nextActions: [
          'HotelMind 品牌规范制定',
          'GoldFactory 品牌规范制定',
          '玄机品牌规范制定',
          '5 大品牌视觉体系统一',
        ],
      },
      {
        id: 'ceo_brain',
        name: 'CEO Brain',
        englishName: 'CEO 决策大脑',
        description: '每日战略建议 + 决策闭环 + 年度目标追踪',
        icon: Brain,
        color: '#F59E0B',
        progress: 40,
        status: 'in_progress',
        metrics: [
          { label: 'CEO 简报', value: '每日运行', target: '稳定 30 天' },
          { label: '年度目标', value: '已分解', target: '可追踪' },
          { label: '决策准确率', value: '40%', target: '80%' },
          { label: '风险预警', value: '基础', target: '全维度' },
        ],
        recentActivities: [
          '年度目标文件创建并接入 CEO 工作台',
          '年度目标分解入口上线（显眼位置）',
          'CEO Advisor 每日简报运行中',
        ],
        nextActions: [
          'CEO Brain 决策准确率提升至 80%',
          '风险预警全维度覆盖',
          '资源分配建议系统上线',
        ],
      },
    ],
  };
}

// ============================================
// 主组件
// ============================================

export default function AssetBuildingDashboard() {
  const [data, setData] = useState<AssetDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDirection, setActiveDirection] = useState<AssetCategory | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 从 Obsidian 加载真实数据（年度目标文件 + Cards 目录统计 + 品牌文件 + 产品白名单）
      const realData = await loadRealDashboardData();
      setData(realData);
    } catch {
      setData(getDefaultDashboardData());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin text-purple-400" />
      </div>
    );
  }

  const activeDir = data.directions.find(d => d.id === activeDirection);

  return (
    <>
      {/* ============ 资产建设仪表板主卡片 ============ */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="relative rounded-xl border border-purple-500/20 bg-gradient-to-br from-purple-500/[0.05] to-blue-500/[0.03] overflow-hidden flex-shrink-0"
      >
        {/* 装饰光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/60 to-transparent" />

        <div className="relative p-4">
          {/* 头部 */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
                <Target size={16} className="text-purple-400" />
              </div>
              <div>
                <h3 className="text-[14px] font-semibold text-white">资产建设仪表板</h3>
                <p className="text-[10px] font-mono text-purple-500/80 uppercase tracking-wider">
                  Phase III · 5 Asset Directions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-gray-500 uppercase">总进度</span>
                <span className="text-[18px] font-mono font-bold text-purple-300">{data.overallProgress}%</span>
              </div>
              <div className="w-24 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${data.overallProgress}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>
          </div>

          {/*// 5 大方向卡片网格（响应式布局，避免遮挡） */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {data.directions.map((dir, i) => {
              const Icon = dir.icon;
              const statusColor =
                dir.status === 'completed' ? 'text-emerald-400' :
                dir.status === 'in_progress' ? 'text-blue-400' :
                dir.status === 'at_risk' ? 'text-red-400' :
                'text-gray-500';
              return (
                <motion.button
                  key={dir.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
                  onClick={() => setActiveDirection(dir.id)}
                  className="text-left rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12] p-2.5 transition-all group"
                  style={{ borderTopColor: `${dir.color}40`, borderTopWidth: 2 }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <Icon size={14} style={{ color: dir.color }} />
                    <span className={`text-[10px] font-mono ${statusColor}`}>
                      {dir.progress}%
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-200 font-medium truncate mb-0.5">
                    {dir.englishName}
                  </div>
                  <div className="text-[9px] font-mono text-gray-600 truncate mb-1.5">
                    {dir.name}
                  </div>
                  <div className="h-0.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${dir.progress}%`, backgroundColor: dir.color }}
                    />
                  </div>
                  <div className="mt-1.5 flex items-center gap-0.5">
                    <ArrowUpRight
                      size={9}
                      className="text-gray-600 group-hover:text-gray-400 transition-colors"
                    />
                    <span className="text-[8px] font-mono text-gray-600 group-hover:text-gray-400">
                      详情
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* 最近活动概览 */}
          <div className="mt-3 pt-3 border-t border-white/[0.04]">
            <div className="flex items-center gap-2 mb-1.5">
              <TrendingUp size={10} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                最近活动
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {data.directions.slice(0, 4).flatMap(d =>
                d.recentActivities.slice(0, 1).map((a, i) => (
                  <div key={`${d.id}-${i}`} className="flex items-center gap-1.5 text-[10px] text-gray-500">
                    <span className="w-1 h-1 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="truncate">{a}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ============ 资产方向详情 Modal ============ */}
      <AnimatePresence>
        {activeDir && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveDirection(null)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border bg-[#0a0e1a] overflow-hidden"
              style={{ borderColor: `${activeDir.color}40` }}
            >
              {/* 头部 */}
              <div
                className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]"
                style={{ background: `linear-gradient(135deg, ${activeDir.color}10, transparent)` }}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center border"
                    style={{
                      backgroundColor: `${activeDir.color}20`,
                      borderColor: `${activeDir.color}40`,
                    }}
                  >
                    <activeDir.icon size={18} style={{ color: activeDir.color }} />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-semibold text-white">
                      {activeDir.englishName} · {activeDir.name}
                    </h3>
                    <p className="text-[10px] font-mono text-gray-500">{activeDir.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[18px] font-mono font-bold" style={{ color: activeDir.color }}>
                      {activeDir.progress}%
                    </div>
                    <div className="text-[9px] font-mono text-gray-600">进度</div>
                  </div>
                  <button
                    onClick={() => setActiveDirection(null)}
                    className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* 内容区 */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {/* 关键指标 */}
                <div>
                  <h4 className="text-[11px] font-mono text-gray-400 uppercase tracking-wider mb-2">
                    关键指标
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {activeDir.metrics.map((m, i) => (
                      <div
                        key={i}
                        className="rounded-md border border-white/[0.04] bg-white/[0.01] px-3 py-2"
                      >
                        <div className="text-[10px] font-mono text-gray-500 uppercase mb-0.5">
                          {m.label}
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-[14px] font-mono font-bold text-gray-200">
                            {m.value}
                          </span>
                          {m.target && (
                            <span className="text-[9px] font-mono text-gray-600">
                              / {m.target}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 最近活动 */}
                <div>
                  <h4 className="text-[11px] font-mono text-gray-400 uppercase tracking-wider mb-2">
                    最近活动
                  </h4>
                  <div className="space-y-1.5">
                    {activeDir.recentActivities.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-[12px] text-gray-300">
                        <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
                        {a}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 下一步行动 */}
                <div>
                  <h4 className="text-[11px] font-mono text-gray-400 uppercase tracking-wider mb-2">
                    下一步行动
                  </h4>
                  <div className="space-y-1.5">
                    {activeDir.nextActions.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-[12px] text-gray-300">
                        <CircleDashed size={11} className="flex-shrink-0" style={{ color: activeDir.color }} />
                        {a}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 底部 */}
              <div className="px-5 py-2.5 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-[10px] font-mono text-gray-600">
                  最后更新：{new Date(data.lastUpdated).toLocaleString('zh-CN')}
                </span>
                <button
                  onClick={() => setActiveDirection(null)}
                  className="flex items-center gap-1 px-3 py-1 text-[11px] font-mono text-gray-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] rounded transition-colors"
                >
                  关闭
                  <ChevronRight size={11} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
